import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { ModalWidth } from '@/config';
import { Button, Col, Divider, Form, Input, InputNumber, message, Modal, Row } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import EnumSelect from '@/components/FormItems/EnumSelect';
import { EncryptionType } from '@/constants';
import { PartnerAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: () => void;
  paymentPartnerId: string;
};

const columns: IColumnType<PartnerAccountDto>[] = [
  {
    title: 'base.IpAddress',
    dataIndex: 'ipAddress',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'base.Port',
    dataIndex: 'port',
    ellipsis: true,
    width: 50,
  },
  {
    title: 'base.UserName',
    dataIndex: 'userName',
    ellipsis: true,
    sorter: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'base.Method',
    dataIndex: 'method',
    ellipsis: true,
  },
  {
    title: 'base.Interface',
    dataIndex: 'interface',
    ellipsis: true,
  },
  {
    title: 'base.SSID',
    dataIndex: 'ssId',
    ellipsis: true,
  },
  {
    title: 'base.SystemProvider',
    dataIndex: 'systemProvider',
    ellipsis: true,
  },
  {
    title: 'base.SystemVersion',
    dataIndex: 'systemVersion',
    ellipsis: true,
  },
];
const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'searchText', label: 'base.UserName', xlSize: 8, xxlSize: 8 }];

export default function ConnectivityDetailsModal({ onClose, paymentPartnerId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string>();
  const createNewConnectivity = (values: any) => {
    setLoading(true);
    apiSdk.PartnerConnectivityService.create({ body: { ...values, partnerId: paymentPartnerId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);

          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateConnectivity = (values: any) => {
    setLoading(true);
    apiSdk.PartnerConnectivityService.update({ body: { ...values, partnerId: paymentPartnerId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setEditingId(undefined);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const createConnectivity = () => {
    setLoading(true);
    apiSdk.PartnerConnectivityService.getDetail({ id: editingId || '' })
      .then(res => {
        if (res) {
          form.setFieldsValue({ ...res });
          setFormVisible(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (editingId) {
      createConnectivity();
    }
  }, [editingId]);
  return (
    <Modal
      style={{ top: 10, minHeight: 500 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('partnerPage_ConnectivityDetails')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/commons/partner-connectivity/get-partner-connectivity-list"
        bordered
        defaultFilter={{
          PartnerId: paymentPartnerId,
        }}
        filters={filters}
        order_by="userName"
        order_by_asc={false}
        allowEdit={true}
        allowDelete={true}
        onEdit={record => {
          setEditingId(record.id);
        }}
        delete_url="/commons/partner-connectivity/delete"
        searchBtnAlignLeft
        extraBtns={
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => {
              form.resetFields();
              setFormVisible(true);
            }}
          >
            {t('createNew')}
          </Button>
        }
      />
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.large}
          className="nta-custom-form"
          title={!editingId ? t('partnerPage_CreatePartner') : t('partnerPage_UpdatePartner')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
            setEditingId(undefined);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateConnectivity(values);
                } else {
                  createNewConnectivity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form
            form={form}
            initialValues={{ encryptionType: EncryptionType.None }}
            size="middle"
            layout="horizontal"
            labelAlign="right"
            labelCol={{ md: 8, xs: 24, xl: 8 }}
          >
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={12}>
                <Form.Item label={t('base.IpAddress')} name="ipAddress">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Port')} name="port">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.UserName')} name="userName">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Password')} name="password">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Method')} name="method">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Interface')} name="interface">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Divider />
                <Form.Item label={t('base.Provider')} name="provider">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.Provider2')} name="provider2">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.PayId')} name="payId">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.PayId2')} name="payId2">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('base.Controller')} name="controller">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.DataType')} name="dataType">
                  <Input />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item label={t('base.SSID')} name="ssId">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.SystemProvider')} name="systemProvider">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.SystemVersion')} name="systemVersion">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Encryption')} name="encryptionType">
                  <EnumSelect enumData={EncryptionType} allowClear={false} />
                </Form.Item>
                <Form.Item label={t('base.DecryptionKey')} name="decryptionKey">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.EncryptionKey')} name="encryptionKey">
                  <Input />
                </Form.Item>
                <Divider />
                <Form.Item label={t('base.OwnPrivateKey')} name="ownPrivateKey">
                  <Input />
                </Form.Item>

                <Form.Item label={t('base.OrangePublicKey')} name="orangePublicKey">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.OperatorId')} name="operatorId">
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.AppId')} name="appId">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </Modal>
  );
}
